import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "@hooks/useAuth";
import {
  notificacaoService,
  Notificacao,
} from "@services/notificacaoService";

const POLL_INTERVAL_MS = 60_000;

/** Som curto de notificação via Web Audio (sem depender de arquivo de áudio). */
function tocarSomNotificacao() {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.09);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
    osc.onended = () => ctx.close();
  } catch {
    // O navegador pode bloquear áudio sem interação prévia — silencioso.
  }
}

// Flash do título da aba como alerta visual de nova notificação.
let tituloFlashTimer: ReturnType<typeof setInterval> | null = null;
let tituloAntesDoFlash: string | null = null;
function alertarTituloNovaNotificacao() {
  const alerta = "🔔 Nova notificação!";
  if (tituloFlashTimer) {
    clearInterval(tituloFlashTimer);
  } else {
    tituloAntesDoFlash = document.title;
  }
  let mostrandoAlerta = true;
  let ciclos = 0;
  tituloFlashTimer = setInterval(() => {
    document.title = mostrandoAlerta
      ? alerta
      : tituloAntesDoFlash ?? document.title;
    mostrandoAlerta = !mostrandoAlerta;
    ciclos++;
    if (ciclos >= 6) {
      if (tituloFlashTimer) clearInterval(tituloFlashTimer);
      tituloFlashTimer = null;
      document.title = tituloAntesDoFlash ?? document.title;
      tituloAntesDoFlash = null;
    }
  }, 700);
}

interface NotificacoesContextValue {
  naoLidas: number;
  notificacoes: Notificacao[];
  carregando: boolean;
  /** Busca a lista completa (chamado ao abrir o painel). */
  carregarLista: () => Promise<void>;
  marcarLida: (id: string) => Promise<void>;
  marcarTodasLidas: () => Promise<void>;
}

const NotificacoesContext = createContext<NotificacoesContextValue | undefined>(
  undefined,
);

/**
 * Provider do centro de notificações (item 6.1). Faz polling leve do contador de
 * não lidas por setTimeout recursivo (não setInterval), pausa quando a aba está
 * em background e só roda quando o usuário está autenticado. Degrada em silêncio:
 * erro de rede nunca quebra o header.
 */
export const NotificacoesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [naoLidas, setNaoLidas] = useState(0);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Última contagem conhecida; null = ainda não inicializado (evita alerta no 1º load). */
  const prevNaoLidasRef = useRef<number | null>(null);

  const atualizarContagem = useCallback(async () => {
    try {
      const n = await notificacaoService.contagem();
      setNaoLidas(n);
      // Alerta (som + flash no título) SOMENTE quando chega algo novo.
      if (prevNaoLidasRef.current !== null && n > prevNaoLidasRef.current) {
        tocarSomNotificacao();
        alertarTituloNovaNotificacao();
      }
      prevNaoLidasRef.current = n;
    } catch {
      // silencioso — tenta de novo no próximo ciclo
    }
  }, []);

  const carregarLista = useCallback(async () => {
    setCarregando(true);
    try {
      const page = await notificacaoService.listar(1, 15);
      setNotificacoes(page.itens);
      setNaoLidas(page.totalNaoLidas);
      prevNaoLidasRef.current = page.totalNaoLidas;
    } catch {
      // silencioso
    } finally {
      setCarregando(false);
    }
  }, []);

  const marcarLida = useCallback(async (id: string) => {
    const agora = new Date().toISOString();
    setNotificacoes((prev) =>
      prev.map((n) => (n.id === id && !n.lidaEm ? { ...n, lidaEm: agora } : n)),
    );
    setNaoLidas((prev) => Math.max(0, prev - 1));
    prevNaoLidasRef.current = Math.max(0, (prevNaoLidasRef.current ?? 1) - 1);
    try {
      await notificacaoService.marcarLida(id);
    } catch {
      // otimista: se falhar, o próximo ciclo/reabertura re-sincroniza
    }
  }, []);

  const marcarTodasLidas = useCallback(async () => {
    const agora = new Date().toISOString();
    setNotificacoes((prev) =>
      prev.map((n) => (n.lidaEm ? n : { ...n, lidaEm: agora })),
    );
    setNaoLidas(0);
    prevNaoLidasRef.current = 0;
    try {
      await notificacaoService.marcarTodasLidas();
    } catch {
      // silencioso
    }
  }, []);

  // Polling recursivo + pausa em background, apenas autenticado.
  useEffect(() => {
    if (!isAuthenticated) {
      setNaoLidas(0);
      setNotificacoes([]);
      prevNaoLidasRef.current = null;
      return;
    }

    let cancelado = false;

    const agenda = () => {
      timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const tick = async () => {
      if (cancelado) return;
      if (document.visibilityState === "visible") {
        await atualizarContagem();
      }
      if (!cancelado) agenda();
    };

    // Primeira leitura imediata, depois agenda o ciclo.
    atualizarContagem();
    agenda();

    const onVisibility = () => {
      if (document.visibilityState === "visible") atualizarContagem();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelado = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, atualizarContagem]);

  const value: NotificacoesContextValue = {
    naoLidas,
    notificacoes,
    carregando,
    carregarLista,
    marcarLida,
    marcarTodasLidas,
  };

  return (
    <NotificacoesContext.Provider value={value}>
      {children}
    </NotificacoesContext.Provider>
  );
};

export const useNotificacoes = (): NotificacoesContextValue => {
  const ctx = useContext(NotificacoesContext);
  if (!ctx) {
    throw new Error(
      "useNotificacoes deve ser utilizado dentro de NotificacoesProvider",
    );
  }
  return ctx;
};
